/**
 * Web-only grouping/ordering helper for the bookings list: which type section an
 * item belongs to, plus its labels/icons. Stay batching, cancelled-last ordering
 * and the bookability predicates live in client-core so the mobile bookings
 * screen reuses them.
 */

import type { ItineraryItem } from '@elara/client-core/models/ItineraryItemModel';

import type { IconName } from '#ui/MaterialIcon.js';

import { arePlacesHydrated, cancelledLast, resolveEventCategory } from '@elara/client-core/lib/bookable-groups';

// Re-exported so this module stays the bookings list's single grouping entry point.
export {
  cancelledLast,
  groupRoomsByStay,
  isBookable,
  isFlightUnavailable,
  isRoomUnavailable,
} from '@elara/client-core/lib/bookable-groups';

export type BookingGroupType = 'flight' | 'room' | 'dining' | 'activity' | 'custom';

export const ITINERARY_TYPE_ORDER: BookingGroupType[] = ['flight', 'room', 'dining', 'activity', 'custom'];

export const TYPE_GROUP_LABELS: Record<BookingGroupType, string> = {
  flight: 'Flights',
  room: 'Hotels',
  dining: 'Dining',
  activity: 'Activities',
  custom: 'Other activities',
};

export const TYPE_GROUP_ICONS: Record<BookingGroupType, IconName> = {
  flight: 'flight_takeoff',
  room: 'hotel',
  dining: 'restaurant',
  activity: 'attractions',
  custom: 'event',
};

export function groupItemsByType(
  items: ItineraryItem[],
): Array<{ type: BookingGroupType; items: ItineraryItem[] }> {
  const groups = new Map<BookingGroupType, ItineraryItem[]>();
  for (const type of ITINERARY_TYPE_ORDER) {
    groups.set(type, []);
  }

  // Until every place item's doc has resolved, they all stay under 'activity' — grouping only
  // stabilizes once the docs land, and rows would otherwise reshuffle as they stream in one-by-one.
  const placesHydrated = arePlacesHydrated(items);

  for (const item of items) {
    let groupType: BookingGroupType;
    if (item.type === 'activity') {
      groupType = 'custom';
    } else if (item.type === 'place') {
      // Places fan out to just two buckets: "Dining" for eateries, "Activities" for the rest — the
      // shared rule mobile's bookings screen groups by too.
      groupType = resolveEventCategory(item, placesHydrated);
    } else {
      groupType = item.type;
    }
    const list = groups.get(groupType);
    if (list) list.push(item);
  }
  return ITINERARY_TYPE_ORDER.map((type) => ({ type, items: cancelledLast(groups.get(type) ?? []) })).filter(
    (g) => g.items.length > 0,
  );
}

